/**
 * useCities — справочник «Город» организации (cities_ref).
 *
 * Точная копия приёмов usePropertyTypes: кэш на модуле, потому что точек
 * чтения много (форма Объекта, фильтр списка, карточка Объекта, настройки), а
 * словарь меняется на порядки реже, чем читается. Правка из соседней вкладки
 * подхватится после reload — компромисс осознанный, тот же что у типов
 * объекта и единиц прайса.
 *
 * ⚠️ Дефолтного списка городов нет и не будет: у каждого тенанта он свой
 * (см. backend/services/cities.js). Пустой справочник — нормальное состояние
 * новой организации, а не признак незагруженных данных: отличать их нужно по
 * loading, а не по длине массива.
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { track } from '../analytics';

let cache = null;          // Promise<City[]>|null
let cachedCanEdit = false; // последнее известное — чтобы новый подписчик не мигал

function load() {
  if (!cache) {
    cache = api.get('/properties/cities-ref')
      .then(r => {
        cachedCanEdit = !!r.can_edit;
        return r.cities || [];
      })
      .catch(() => []);
  }
  return cache;
}

/** Сбросить кэш — после добавления/переименования/архивации города. */
export function invalidateCities() {
  cache = null;
}

export function useCities({ enabled = true } = {}) {
  const [cities, setCities] = useState([]);
  const [canEdit, setCanEdit] = useState(cachedCanEdit);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    load().then(c => {
      if (!alive) return;
      setCities(c || []);
      setCanEdit(cachedCanEdit);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [enabled]);

  const reload = async () => {
    invalidateCities();
    const c = await load();
    setCities(c || []);
    setCanEdit(cachedCanEdit);
    return c;
  };

  const addCity = async (label) => {
    const r = await api.post('/properties/cities-ref', { label_ru: label });
    track('city_edit', { action: r.created ? 'add' : 'reuse' });
    await reload();
    return r.city;
  };

  const renameCity = async (cityId, label) => {
    const row = await api.put(`/properties/cities-ref/${cityId}`, { label_ru: label });
    track('city_edit', { action: 'rename' });
    invalidateCities();
    setCities(prev => prev.map(c => (c.id === cityId ? { ...c, ...row } : c)));
    return row;
  };

  /**
   * Архивация. Сервер отвечает 409 CITY_IN_USE, если на городе висят Объекты
   * (см. backend/routes/properties.js) — ошибку не глотаем, вызывающий показывает
   * тенанту, сколько именно Объектов нужно перенести.
   */
  const archiveCity = async (cityId) => {
    await api.delete(`/properties/cities-ref/${cityId}`);
    track('city_edit', { action: 'archive' });
    invalidateCities();
    setCities(prev => prev.filter(c => c.id !== cityId));
  };

  // Драг-н-дроп в Настройках — тот же приём, что reorderTypes в
  // usePropertyTypes: стейт уже переставлен вызывающим, здесь фиксируем на
  // сервере и сбрасываем модульный кэш, чтобы форма Объекта и фильтры не
  // отдавали старый порядок до следующей перезагрузки страницы.
  const reorderCities = async (reordered) => {
    setCities(reordered);
    try {
      await api.patch('/properties/cities-ref/reorder', { ids: reordered.map(c => c.id) });
      track('city_edit', { action: 'reorder' });
      invalidateCities();
    } catch (e) {
      await reload(); // откат к серверному порядку
      throw e;
    }
  };

  return { cities, loading, canEdit, reload, addCity, renameCity, archiveCity, reorderCities };
}

/** Ярлык города на языке интерфейса с честным фолбэком на второй. */
export function cityLabel(row, lang) {
  if (!row) return '';
  return (lang === 'en' ? (row.label_en || row.label_ru) : (row.label_ru || row.label_en)) || '';
}

/** Лейбл по id — для списков и карточек Объекта. */
export function cityLabelById(cities, cityId, lang) {
  if (!cityId) return '';
  return cityLabel((cities || []).find(c => c.id === Number(cityId)), lang);
}
